import { Fragment } from 'react';
import { BookMarked } from 'lucide-react';
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ErrorReportButton } from '@/components/ErrorReportPanel';
import { BLOCK_ICON } from '@/constants/blockIcons';
import { NO_CATEGORY, useInsertableLibraryPageGroups, usePageLibraryStore } from '@/store/pageLibraryStore';
import type { Template } from '@/types';

/** Menu's met de bibliotheek worden lang; ze scrollen binnen het scherm. */
export const LIBRARY_MENU_CONTENT_CLASS =
  'max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto';

/** Zonder pagina's: laden, de fout wanneer de lijst niet kon laden (het volgende menu probeert opnieuw), of de uitleg. */
function EmptyHint() {
  const loaded = usePageLibraryStore((state) => state.loaded);
  const error = usePageLibraryStore((state) => state.error);
  return (
    <p className="px-2 py-1.5 text-xs text-muted-foreground">
      {error ? (
        <>
          {error.message}. Open het menu opnieuw om het nog eens te proberen.{' '}
          <ErrorReportButton report={error.report} />
        </>
      ) : !loaded ? (
        'Pagina’s laden…'
      ) : (
        'De bibliotheek is leeg. Maak een pagina onder Pagina’s in het overzicht, of bewaar er een met het bladwijzer-icoon in de paginalijst.'
      )}
    </p>
  );
}

/** Kopjes alleen als er iets te onderscheiden valt; één groep zonder categorie is gewoon een lijst. */
function useMenuGroups() {
  const groups = useInsertableLibraryPageGroups();
  const showHeaders = groups.length > 1 || (groups.length === 1 && groups[0][0] !== NO_CATEGORY);
  return { groups, showHeaders };
}

function PageIcon({ page }: { page: Template }) {
  const Icon = BLOCK_ICON[page.blocks[0]?.type] ?? BookMarked;
  return (
    <span className="mr-2 text-muted-foreground">
      <Icon className="h-4 w-4" />
    </span>
  );
}

function PageMeta({ page }: { page: Template }) {
  if (page.blocks.length < 2) return null;
  return <span className="ml-2 shrink-0 text-[11px] text-muted-foreground">{page.blocks.length} pagina's</span>;
}

/** De bibliotheek, per categorie, als inhoud van een "Pagina toevoegen"-dropdown. */
export function LibraryPageMenuItems({ onSelect }: { onSelect: (page: Template) => void }) {
  const { groups, showHeaders } = useMenuGroups();
  if (groups.length === 0) return <EmptyHint />;

  return (
    <>
      {groups.map(([category, pages], index) => (
        <Fragment key={category}>
          {index > 0 && <DropdownMenuSeparator />}
          {showHeaders && (
            <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">{category}</DropdownMenuLabel>
          )}
          {pages.map((page) => (
            <DropdownMenuItem key={page.id} onSelect={() => onSelect(page)} title={page.description}>
              <PageIcon page={page} />
              <span className="min-w-0 flex-1 truncate">{page.name}</span>
              <PageMeta page={page} />
            </DropdownMenuItem>
          ))}
        </Fragment>
      ))}
    </>
  );
}

/** Zelfde lijst voor het invoegmenu tussen twee pagina's, dat geen dropdown is. */
export function LibraryPageButtons({ onSelect }: { onSelect: (page: Template) => void }) {
  const { groups, showHeaders } = useMenuGroups();
  if (groups.length === 0) return <EmptyHint />;

  return (
    <>
      {groups.map(([category, pages], index) => (
        <Fragment key={category}>
          {index > 0 && <div className="my-1 h-px bg-muted" />}
          {showHeaders && <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{category}</div>}
          {pages.map((page) => (
            <button
              key={page.id}
              type="button"
              title={page.description}
              className="flex w-full items-center px-2 py-1.5 text-sm hover:bg-accent rounded-sm cursor-pointer text-left"
              onClick={() => onSelect(page)}
            >
              <PageIcon page={page} />
              <span className="min-w-0 flex-1 truncate">{page.name}</span>
              <PageMeta page={page} />
            </button>
          ))}
        </Fragment>
      ))}
    </>
  );
}
